import { useEffect, useState, useMemo } from "react";
import { IAlertEvent } from "@/domain/alertEvent.interface";
import { EVENT_ENUM } from "../../domain/events.enum";
import { IUserRole } from "../../services/user.service";
import { useUserStore } from "../../stores/user.store";
import { useGetBeneficiaries } from "../useBeneficiaries";
import useDate from "../useDate";
import {
	addBeneficiariesCompletedEvents,
	addBeneficiariesDisenrollEvents,
	addBeneficiariesEnrollEvents,
	addBeneficiariesMissedEntryEvents,
	addBeneficiariesCertificateAvailableEvents,
	addBeneficiariesReconfirmationEvents,
	addBeneficiaries31DaysAbsEvents,
} from "./AlertAndEventsHelper";
import { useGetAlertAndEvents } from "../../hooks/useAlertAndEvents";

export const useSetupEvents = (userInfo: IUserRole) => {
	const { userHasAccessToCode } = useUserStore();
	const [events, setEvents] = useState<IAlertEvent[]>([]);
	const { dayjs } = useDate();

	const programs = useMemo(() => {
		return userInfo?.programs.reduce((acc, program) => {
			return acc ? `${acc},${program.programId}` : program.programId;
		}, "");
	}, [userInfo]);

	const userProgramOuPairs = useMemo(() => {
		const pairs = new Set<string>();
		userInfo?.programs.forEach((program) => {
			// orgUnitId is a plain string[] at runtime despite the type definition
			(program.orgUnitId as unknown as string[]).forEach((ouId) => {
				pairs.add(`${program.programId}:${ouId}`);
			});
		});
		return pairs;
	}, [userInfo]);

	const initialFilters = useMemo(
		() => ({
			programId: programs,
			providerId: "",
			providerSiteId: [],
			status: ["ACTIVE", "COMPLETED", "PAUSED", "DISENROLLED"],
			search: "",
			pageNumber: 0,
			pageSize: 1000,
		}),
		[programs],
	);

	const {
		data: beneficiaryList,
		isLoading: benefLoading,
		error,
		refetch: refetchBeneficiaryList,
	} = useGetBeneficiaries({
		programId: initialFilters.programId,
		providerSite: initialFilters.providerSiteId,
		providerId: initialFilters.providerId,
		status: initialFilters.status,
		search: initialFilters.search,
		pageNumber: initialFilters.pageNumber + 1,
		pageSize: initialFilters.pageSize,
		userId: userInfo?.id,
	});

	const {
		data: backendAlerts,
		isLoading: backendLoading,
		isFetching: backendFetching,
		error: backendError,
		refetch: refetchBackendAlerts,
	} = useGetAlertAndEvents(userInfo?.id);

	useEffect(() => {
		if (initialFilters.programId) {
			refetchBeneficiaryList();
			refetchBackendAlerts();
		}
	}, [initialFilters.programId, refetchBeneficiaryList, refetchBackendAlerts]);

	const filteredBeneficiaries = useMemo(
		() =>
			(beneficiaryList?.data ?? []).filter((b) =>
				userProgramOuPairs.has(
					`${b.lastEnrollment?.programId}:${b.lastEnrollment?.orgUnitId}`,
				),
			),
		[beneficiaryList, userProgramOuPairs],
	);

	useEffect(() => {
		if (!benefLoading && beneficiaryList && !backendLoading && backendAlerts) {
			const newEvents: IAlertEvent[] = [];

			for (const event in EVENT_ENUM) {
				switch (event) {
					case EVENT_ENUM.EVE_BEN_ENROLL:
						if (userHasAccessToCode(EVENT_ENUM.EVE_BEN_ENROLL)) {
							addBeneficiariesEnrollEvents(
								newEvents,
								filteredBeneficiaries,
								dayjs,
								EVENT_ENUM.EVE_BEN_ENROLL,
							);
						}
						break;
					case EVENT_ENUM.EVE_BEN_DISENROLL:
						if (userHasAccessToCode(EVENT_ENUM.EVE_BEN_DISENROLL)) {
							addBeneficiariesDisenrollEvents(
								newEvents,
								filteredBeneficiaries,
								dayjs,
								EVENT_ENUM.EVE_BEN_DISENROLL,
							);
						}
						break;
					case EVENT_ENUM.EVE_MISSED_ENTRY:
						if (userHasAccessToCode(EVENT_ENUM.EVE_MISSED_ENTRY)) {
							addBeneficiariesMissedEntryEvents(
								newEvents,
								filteredBeneficiaries,
								dayjs,
								EVENT_ENUM.EVE_MISSED_ENTRY,
							);
						}
						break;
					case EVENT_ENUM.EVE_BEN_COMPL:
						if (userHasAccessToCode(EVENT_ENUM.EVE_BEN_COMPL)) {
							addBeneficiariesCompletedEvents(
								newEvents,
								filteredBeneficiaries,
								dayjs,
								EVENT_ENUM.EVE_BEN_COMPL,
							);
							addBeneficiariesCertificateAvailableEvents(
								newEvents,
								filteredBeneficiaries,
								dayjs,
								EVENT_ENUM.EVE_BEN_COMPL,
							);
						}
						break;
					case EVENT_ENUM.EVE_CM_ELIG:
						if (userHasAccessToCode(EVENT_ENUM.EVE_CM_ELIG)) {
							addBeneficiariesReconfirmationEvents(
								newEvents,
								filteredBeneficiaries,
								dayjs,
								EVENT_ENUM.EVE_CM_ELIG,
							);
						}
						break;
					case EVENT_ENUM.EVE_31DAYS_ABS:
						if (userHasAccessToCode(EVENT_ENUM.EVE_31DAYS_ABS)) {
							addBeneficiaries31DaysAbsEvents(
								newEvents,
								filteredBeneficiaries,
								dayjs,
								EVENT_ENUM.EVE_31DAYS_ABS,
							);
						}
						break;
					default:
						break;
				}
			}

			// Exclude backend alerts for action codes already handled as frontend events
			const frontendHandledCodes = new Set([EVENT_ENUM.EVE_BEN_ENROLL]);
			// Backend alerts have populated objects: programId is an object with _id, orgUnitId is a plain string
			const filteredAlerts = backendAlerts.filter((alert) => {
				if (
					frontendHandledCodes.has(alert.actionCode as EVENT_ENUM) ||
					alert.actionCode === ""
				) {
					return false;
				}
				const alertProgramId =
					typeof alert.programId === "object"
						? (alert.programId as any)?._id // eslint-disable-line @typescript-eslint/no-explicit-any
						: alert.programId;
				return userProgramOuPairs.has(`${alertProgramId}:${alert.orgUnitId}`);
			});
			newEvents.push(...filteredAlerts);

			setEvents(newEvents);
		}
		if (error || backendError) {
			// TODO: if needed handle scenario instead of logging a console console.error(error);
		}
	}, [
		beneficiaryList,
		benefLoading,
		backendAlerts,
		backendLoading,
		error,
		backendError,
		userHasAccessToCode,
		dayjs,
	]);

	return { events, refetchBackendAlerts, isFetching: backendFetching };
};
